import { PrivacyChoiceField, type PrivacyChoice } from './Privacy';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@dru/api-client';
import { useAuth, errorMessage } from '../lib/auth';
import { BrandPanel, MobileBrandBar } from './LoginBrand';
import { BRAND } from '@dru/types';

/**
 * OTP sign-in, in two steps.
 *
 * The registered mobile number, and the employee code only when that number
 * is on more than one employee's record (a shared or a supervisor's handset).
 * Both used to be required (D17), but nothing ever sent an employee their code
 * and sign-in stopped there (flow audit 2026-10-20, 6.1). The code still lands
 * only on the number of record, and the server answers the same way whether or
 * not the number is known.
 *
 * The data-use notice is asked once per version, after the code proves who is
 * signing in — not ticked again at every sign-in. The server says when it is
 * needed (`privacy-consent-required`), and the same code is sent again with
 * the answer.
 *
 * Written for a low-end phone held in a factory: 16px inputs so iOS does not
 * zoom on focus, numeric keypads, one action per step, and no jargon.
 */
export function Login() {
  const { requestOtp, verifyOtp } = useAuth();
  const nav = useNavigate();

  const [step, setStep] = useState<'identify' | 'code'>('identify');
  const [employeeCode, setEmployeeCode] = useState('');
  const [mobile, setMobile] = useState('');
  const [challengeToken, setChallengeToken] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState<PrivacyChoice | null>(null);
  /** The server asked for the data-use choice: this employee has not agreed to the current notice. */
  const [consentAsked, setConsentAsked] = useState(false);
  // A testing build accepts a fixed code, and there the server asks for the
  // employee code too (the code on the handset proves nothing), so the field
  // starts open.
  const [withCode, setWithCode] = useState(import.meta.env.VITE_DEMO_LOGINS === 'true');
  const [secondsLeft, setSecondsLeft] = useState(0);

  const codeRef = useRef<HTMLInputElement>(null);

  // A visible countdown, because the code really does expire in five minutes
  // and a worker who does not know that will keep trying a dead one.
  useEffect(() => {
    if (step !== 'code' || secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [step, secondsLeft]);

  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
  }, [step]);

  async function sendCode(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await requestOtp(withCode ? employeeCode.trim() || null : null, mobile.trim());
      setChallengeToken(res.challengeToken);
      setSentTo(res.sentTo);
      setSecondsLeft(300);
      setStep('code');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    if (consentAsked && !privacyConsent) {
      setError('Please read the data-use notice and tick the box to continue.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await verifyOtp(challengeToken, code.trim(), consentAsked ? privacyConsent : null);
      nav('/home', { replace: true });
    } catch (err) {
      // The code was right; this employee has not agreed to the current
      // notice. Asked here, once, and the same code goes back with the answer.
      if (err instanceof ApiError && err.type === 'privacy-consent-required') {
        setConsentAsked(true);
        return;
      }
      setError(errorMessage(err));
      setCode('');
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  const mm = String(Math.floor(secondsLeft / 60));
  const ss = String(secondsLeft % 60).padStart(2, '0');

  return (
    <div className="flex min-h-dvh flex-col bg-white lg:flex-row">
      <BrandPanel />
      <MobileBrandBar />

    <main className="flex flex-1 flex-col">
    <div className="flex flex-1 items-start justify-center px-5 pb-8 pt-7 sm:px-8 lg:items-center lg:py-12">
    <div className="w-full max-w-[420px] animate-rise-in">
      <div className="mb-7">
        {/* A worker arrives here from an SMS link, on their own phone, and the
            first question they have is whether this is really their employer's
            health app or something phishing them for an OTP. The mark answers
            that before the heading does — in the brand band on a phone, and
            here beside the form on a wide screen. */}
        <img
          src="/logo.png"
          alt={BRAND.logo.alt}
          width={200}
          height={41}
          className="mb-6 hidden h-[41px] w-auto object-contain lg:block"
        />
        <h1 className="text-2xl font-bold text-neutral-900">My Health</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Your workplace health checkups, reports and fitness certificate.
        </p>
      </div>

      {step === 'identify' ? (
        <form onSubmit={sendCode} noValidate className="space-y-5">
          <label className="block">
            <span className="text-sm font-medium text-neutral-800">Mobile number</span>
            <input
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              maxLength={10}
              placeholder="The number your employer has"
              required
              autoFocus
              /* 16px minimum, or iOS zooms the page on focus. */
              className="mt-2 w-full rounded-lg border border-neutral-300 px-4 py-3 text-base tracking-wide
                         focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
            />
          </label>

          {/* Only for a number on more than one employee's record. Out of the
              way for everybody else, and one tap away for them. */}
          {withCode ? (
            <label className="block">
              <span className="text-sm font-medium text-neutral-800">
                Employee code <span className="font-normal text-neutral-500">(optional)</span>
              </span>
              <input
                value={employeeCode}
                onChange={(e) => setEmployeeCode(e.target.value)}
                autoComplete="username"
                autoCapitalize="characters"
                placeholder="On your ID card and appointment SMS"
                className="mt-2 w-full rounded-lg border border-neutral-300 px-4 py-3 text-base
                           focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
              />
            </label>
          ) : (
            <button
              type="button"
              onClick={() => setWithCode(true)}
              className="-ml-2 min-h-11 rounded-lg px-2 text-sm text-primary-600 underline underline-offset-2 active:bg-neutral-100"
            >
              Another employee uses this number? Add your employee code
            </button>
          )}

          {error && (
            <div role="alert" className="rounded-lg bg-danger-100 px-4 py-3 text-sm text-danger-600">
              {error}
            </div>
          )}

          {import.meta.env.VITE_DEMO_LOGINS === 'true' && (
            <p className="rounded-lg bg-warning-100 px-3 py-2 text-xs text-warning-600">
              Testing build: try <span className="font-mono">DEMO-0001</span> with mobile{' '}
              <span className="font-mono">9876500001</span>; the code{' '}
              <span className="font-mono">111111</span> is accepted.
            </p>
          )}

          <button
            type="submit"
            disabled={busy || mobile.length !== 10}
            className="w-full rounded-lg bg-gradient-to-r from-brand-navy to-brand-magenta py-3.5 text-base font-semibold text-white shadow-card
                       transition hover:brightness-[1.07] active:brightness-95 disabled:from-neutral-300 disabled:to-neutral-300 disabled:shadow-none"
          >
            {busy ? 'Sending…' : 'Send code'}
          </button>
        </form>
      ) : (
        <form onSubmit={submitCode} noValidate className="space-y-5">
          <div>
            <h2 className="text-base font-medium text-neutral-900">Enter the code</h2>
            <p className="mt-1 text-sm text-neutral-700">
              If <span className="font-medium">{sentTo}</span> is the number on your record, a 6-digit
              code is on its way by SMS.
            </p>
            {!withCode && (
              <p className="mt-1 text-xs text-neutral-600">
                No code? If another employee uses this number too, go back and add your employee code.
              </p>
            )}
          </div>

          <input
            ref={codeRef}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            aria-label="6-digit code"
            className="w-full rounded-lg border border-neutral-300 px-4 py-4 text-center text-2xl
                       font-semibold tracking-[0.4em] focus:border-primary-500 focus:outline-none
                       focus:ring-2 focus:ring-primary-500/30"
          />

          {import.meta.env.VITE_DEMO_LOGINS === 'true' && (
            <p className="text-center text-xs text-warning-600">Testing build: 111111 works.</p>
          )}

          <p className="text-center text-xs text-neutral-600">
            {secondsLeft > 0
              ? `Expires in ${mm}:${ss}`
              : 'That code has expired. Ask for a new one.'}
          </p>

          {/* Asked once, after the code has shown who is signing in, and
              only when this employee has not agreed to this version yet. */}
          {consentAsked && (
            <div className="rounded-lg border border-neutral-200 p-3">
              <p className="mb-1 text-sm font-medium text-neutral-900">One more step, the first time</p>
              <PrivacyChoiceField value={privacyConsent} onChange={setPrivacyConsent} />
            </div>
          )}

          {error && (
            <div role="alert" className="rounded-lg bg-danger-100 px-4 py-3 text-sm text-danger-600">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || code.length !== 6 || (consentAsked && !privacyConsent)}
            className="w-full rounded-lg bg-gradient-to-r from-brand-navy to-brand-magenta py-3.5 text-base font-semibold text-white shadow-card
                       transition hover:brightness-[1.07] active:brightness-95 disabled:from-neutral-300 disabled:to-neutral-300 disabled:shadow-none"
          >
            {busy ? 'Checking…' : consentAsked ? 'Agree and sign in' : 'Sign in'}
          </button>

          <div className="flex items-center justify-between gap-2 text-sm">
            <button
              type="button"
              onClick={() => { setStep('identify'); setCode(''); setError(null); setConsentAsked(false); setPrivacyConsent(null); }}
              className="-ml-2 min-h-11 rounded-lg px-2 text-neutral-700 underline underline-offset-2 active:bg-neutral-100"
            >
              Change details
            </button>
            <button
              type="button"
              onClick={() => sendCode()}
              disabled={busy || secondsLeft > 240}
              className="-mr-2 min-h-11 rounded-lg px-2 text-primary-600 underline underline-offset-2 active:bg-neutral-100
                         disabled:text-neutral-500 disabled:no-underline"
            >
              {/* Held for the first minute: the server allows three codes per
                  fifteen minutes, and letting someone burn all three in ten
                  seconds locks them out of their own app. */}
              {secondsLeft > 240 ? `Resend in ${secondsLeft - 240}s` : 'Resend code'}
            </button>
          </div>
        </form>
      )}

      <p className="mt-8 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-center text-xs text-neutral-500">
        Trouble signing in? Ask your HR team to check the mobile number on your record.
      </p>
    </div>
    </div>
    <p className="px-5 pb-5 text-center text-xs text-neutral-500 sm:px-8">
      © {new Date().getFullYear()} {BRAND.longName}
    </p>
    </main>
    </div>
  );
}
